import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import {
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import MuiAlert, { AlertProps, type Color } from '@material-ui/lab/Alert';
import { Link } from 'react-router-dom';
import { api } from '@services/api';
import { Autocomplete } from '@material-ui/lab';
import { ASO } from '@data/tipoAso';
import { status } from '@data/status';
import { useMutation } from '@tanstack/react-query';
import { AddCircleOutline as AddCircleOutlineIcon } from '@material-ui/icons';
import {
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon
} from '@material-ui/icons';
import InputMask from 'react-input-mask';
import { Delete as DeleteIcon } from '@material-ui/icons';
import { IStatus } from '.';

// Interfaces
interface ISelectedExames {
  id_exame_ghe: number;
  cod_ghe: string;
  nome: string;
}

interface IExameHibrido {
  id_exame_ghe: number;
  cod_ghe: string;
  nome: string;
}

interface IColaborador {
  id_colaboradores: number;
  nome_completo: string;
  cpf_colaborador: string;
}

interface INomeComercial {
  id_clinicas_credenciadas: number;
  nome_comercial: string;
  cpf_cnpj: string;
  cidade: string;
  estado: string;
  tipo: string;
}

export interface CredentialProps {
  tipo_agendamento: ReactNode;
  id_exame_hibrido: number;
  cpf_cnpj_comer: string;
  horario: string;
  data_agendamento: string;
  nome_comercial: string;
  ordem_chegada: string | null;
  exames: ISelectedExames[];
  linkDoatendimento?: string;
  tipo: string;
}
interface TransferListProps {
  title: string;
  items: ISelectedExames[];
  checked: ISelectedExames[];
  onToggle: (item: ISelectedExames) => () => void;
  onToggleAll: (items: ISelectedExames[]) => void; // Mudei o tipo de retorno
}
// Estilos
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f9f9f9'
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  title: {
    marginBottom: theme.spacing(4),
    fontWeight: 'bold',
    color: '#2c3e50',
    position: 'relative',
    paddingBottom: theme.spacing(1),
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 80,
      height: 4,
      backgroundColor: '#0D9C5B',
      borderRadius: 2
    }
  },
  sectionTitle: {
    margin: `${theme.spacing(3)}px 0 ${theme.spacing(2)}px`,
    fontWeight: 600,
    color: '#2c3e50'
  },
  formRow: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    '& > *': {
      flex: '1 1 200px'
    }
  },
  transferContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
    margin: `${theme.spacing(3)}px 0`
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
    gap: theme.spacing(2)
  },
  examCard: {
    flex: 1,
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    overflow: 'hidden'
  },
  examHeader: {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderBottom: '1px solid #e0e0e0'
  },
  examList: {
    maxHeight: 300,
    overflowY: 'auto'
  },
  examItem: {
    '&:hover': {
      backgroundColor: '#f9f9f9'
    },
    '&.Mui-selected': {
      backgroundColor: '#f0f7f4'
    }
  },
  buttonPrimary: {
    backgroundColor: '#0D9C5B',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0a7a4a'
    }
  },
  buttonSecondary: {
    borderColor: '#0D9C5B',
    color: '#0D9C5B'
  },
  transferButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  transferButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    borderRadius: '50%'
  }
}));

// Componente de lista de transferência
const TransferList = ({
  title,
  items,
  checked,
  onToggle,
  onToggleAll
}: TransferListProps) => {
  const classes = useStyles();
  const count = items.filter((item) => checked.includes(item)).length;

  return (
    <Card className={classes.examCard}>
      <div className={classes.examHeader}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {items.length} itens ({count} selecionados)
        </Typography>
      </div>
      <List className={classes.examList} dense>
        {items.length > 0 && (
          <ListItem button onClick={() => onToggleAll(items)}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={count === items.length && items.length !== 0}
                indeterminate={count > 0 && count < items.length}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary="Selecionar todos" />
          </ListItem>
        )}
        {items.map((item) => (
          <ListItem
            key={item.id_exame_ghe}
            button
            onClick={onToggle(item)}
            className={classes.examItem}
            selected={checked.includes(item)}
          >
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={checked.includes(item)}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText
              primary={item.nome}
              secondary={`Código: ${item.cod_ghe}`}
            />
          </ListItem>
        ))}
      </List>
    </Card>
  );
};

export const AtendimentoHibrido:React.FC<IStatus> = (props) => {
  const classes = useStyles();
  const [statusAgendamento, setStatusAgendamento] = useState('Solicitado');
  const [cpfColab, setCpfColab] = useState('');
  const [nomeColab, setNomeColab] = useState('');
  const [tipoAso, setTipoAso] = useState('');
  const [obs, setObs] = useState('');
  const [atendimento, setAtendimento] = useState('');
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [horarioAgendamento, setHorarioAgendamento] = useState('');
  const [ordemChegada, setOrdemChegada] = useState('Sim');
  const [linkDoAtendimento, setLinkDoAtendimento] = useState('');
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Color>('success');
  const [isHideCredential, setIsHideCredential] = useState(false);
  const [loadingExames, setLoadingExames] = useState(false);

  // Estados para a lista de transferência
  const [checked, setChecked] = useState<ISelectedExames[]>([]);
  const [left, setLeft] = useState<ISelectedExames[]>([]);
  const [right, setRight] = useState<ISelectedExames[]>([]);
  const [credentialArray, setCredentialArray] = useState<CredentialProps[]>([]);
  const [selectedNomeComercial, setSelectedNomeComercial] =
    useState<INomeComercial | null>(null);

  const { mutateAsync: fetchExames } = useMutation({
    mutationFn: async (data: { cpf_colaborador: string; tipo_aso: string }) => {
      const response = await api.post('/ravim/exames-hibrido', data);
      return response.data; // Retorna diretamente os dados
    },
    onError: (error) => {
      console.error('Erro ao buscar exames:', error);
      setLoadingExames(false);
      setSnack(true);
      setSeverity('error');
      setMessage('Erro ao carregar exames disponíveis');
    }
  });

  // Estados
  const [loadingColab, setLoadingColab] = useState(false);
  const [messageError, setMessageError] = useState('');

  // Função para buscar colaborador com máscara de CPF
  const handleCpfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value.replace(/\D/g, ''); // Remove formatação
    const cpfFormatado = valor.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    setCpfColab(cpfFormatado);
    setMessageError('');
  
    if (cpfFormatado.length === 14) { // CPF completo
      setLoadingColab(true);
      try {
        const response = await api.post('/ravim/pesq-cpf', {
          cpf_colaborador: cpfFormatado // Envia sem formatação
        });
        
        if (response.data.code === 200) {
          setNomeColab(response.data.nome_completo);
        } else {
          setMessageError(response.data.message);
          setNomeColab('');
        }
      } catch (error: any) {
        const errorMessage = getErrorMessage(error);
        setMessageError(errorMessage);
        setNomeColab('');
      } finally {
        setLoadingColab(false);
      }
    } else if (cpfFormatado.length === 0) {
      setNomeColab('');
    }
  };

  const errorMessages: Record<number, string> = {
    400: 'CPF inválido. Verifique o número digitado.',
    404: 'Colaborador não encontrado. Verifique o CPF ou cadastre o colaborador.',
    500: 'Erro interno no servidor. Tente novamente mais tarde.',
    // Você pode adicionar outros códigos específicos aqui
  };
  
  const getErrorMessage = (error: any): string => {
    // Verifica primeiro se há uma mensagem específica da API
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    
    // Depois verifica por códigos de status HTTP
    if (error?.response?.code) {
      return errorMessages[error.response.status] || `erro: ${error.response.status}`;
    }
    
    // Erro genérico (rede, etc)
    return 'Não foi possível conectar ao servidor. Verifique sua conexão.';
  };

  // Mutação para buscar credenciados
  const { data: returListaNomeComercial, mutate } = useMutation({
    mutationFn: async (text: object) => {
      return await api.post(`/ravim/telemed-clinicas`, text);
    }
  });
  const handleRemoveCredential = (id: number) => {
    const credentialToRemove = credentialArray.find(
      (cred) => cred.id_exame_hibrido === id
    );
    if (credentialToRemove) {
      // Remove a credencial
      setCredentialArray(
        credentialArray.filter((cred) => cred.id_exame_hibrido !== id)
      );

      // Adiciona os exames de volta à lista left (disponíveis)
      const examesAdicionados = getExamesAdicionados();
      const examesToAddBack = credentialToRemove.exames.filter(
        (exame) =>
          !examesAdicionados.some(
            (added) => added.id_exame_ghe === exame.id_exame_ghe
          )
      );

      setLeft((prevLeft) => [...prevLeft, ...examesToAddBack]);
      setRight((prevRight) =>
        prevRight.filter(
          (item) =>
            !credentialToRemove.exames.some(
              (ex) => ex.id_exame_ghe === item.id_exame_ghe
            )
        )
      );
      setChecked((prevChecked) =>
        prevChecked.filter(
          (item) =>
            !credentialToRemove.exames.some(
              (ex) => ex.id_exame_ghe === item.id_exame_ghe
            )
        )
      );
    }
  };

  // Funções de manipulação da lista de transferência
  const handleToggle = (value: ISelectedExames) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleToggleAll = (items: ISelectedExames[]) => {
    const selected = items.filter((item) => checked.includes(item));
    if (selected.length === items.length) {
      setChecked(checked.filter((item) => !items.includes(item)));
    } else {
      setChecked([
        ...checked,
        ...items.filter((item) => !checked.includes(item))
      ]);
    }
  };

  const handleCheckedRight = () => {
    const selected = left.filter((item) => checked.includes(item));
    setRight([...right, ...selected]);
    setLeft(left.filter((item) => !selected.includes(item)));
    setChecked(checked.filter((item) => !selected.includes(item)));
  };

  const handleCheckedLeft = () => {
    const selected = right.filter((item) => checked.includes(item));
    setLeft([...left, ...selected]);
    setRight(right.filter((item) => !selected.includes(item)));
    setChecked(checked.filter((item) => !selected.includes(item)));
  };

  // Função para adicionar credencial
  const handleAddCredential = () => {
    if (!selectedNomeComercial || right.length === 0) return;

    const newCredential: CredentialProps = {
      id_exame_hibrido: Date.now(),
      cpf_cnpj_comer: selectedNomeComercial.cpf_cnpj,
      horario: horarioAgendamento,
      data_agendamento: dataAgendamento,
      nome_comercial: selectedNomeComercial.nome_comercial,
      ordem_chegada: atendimento === 'presencial' ? ordemChegada : null,
      exames: [...right],
      linkDoatendimento: atendimento === 'telemedicina' ? linkDoAtendimento : undefined,
      tipo: atendimento,
      tipo_agendamento: undefined
    };

    setCredentialArray([...credentialArray, newCredential]);
    setRight([]);
    setChecked([]);

    // Remove os exames adicionados da lista left
    setLeft((prevLeft) =>
      prevLeft.filter(
        (item) => !right.some((ex) => ex.id_exame_ghe === item.id_exame_ghe)
      )
    );
  };

  // Função para enviar agendamento
  const sendAgendamento = async () => {
    try {
      const response = await api.post('/ravim/agendamento-hibrido', {
        status: statusAgendamento,
        cpf_colaborador: cpfColab,
        tipo_aso: tipoAso,
        observacao: obs,
        exame_colab: credentialArray
      });

      setSnack(true);
      setSeverity('success');
      setMessage(response.data.message);

      setTimeout(() => {
        window.location.replace('/agendamentos');
      }, 1000);
    } catch (error) {
      setSnack(true);
      setSeverity('error');
      setMessage('Erro ao enviar agendamento');
    }
  };
  const getExamesAdicionados = useCallback(() => {
    return credentialArray.flatMap((cred) => cred.exames);
  }, [credentialArray]);

  useEffect(() => {
    // Quando o tipo de atendimento muda, limpa todas as seleções
    setLeft([]);
    setRight([]);
    // setChecked([]);
    // setCredentialArray([]);
    setSelectedNomeComercial(null);
    setDataAgendamento('');
    setHorarioAgendamento('');
    setOrdemChegada('Sim');
    setLinkDoAtendimento('');
    // Se já tínhamos exames carregados, mantemos apenas os não adicionados
    if (left.length > 0) {
      const examesAdicionados = getExamesAdicionados();
      setLeft(
        left.filter(
          (exame) =>
            !examesAdicionados.some(
              (added) => added.id_exame_ghe === exame.id_exame_ghe
            )
        )
      );
    }
  }, [atendimento]);

  // Efeitos
  useEffect(() => {
    if (statusAgendamento && cpfColab && tipoAso) {
      setIsHideCredential(true);
    } else {
      setIsHideCredential(false);
    }
  }, [statusAgendamento, cpfColab, tipoAso]);

  useEffect(() => {
    if (atendimento) {
      mutate({
        tipo_agendamento: props.location.state.tipo_agendamento,
        tipo: atendimento
      });
    }
  }, [atendimento]);

  useEffect(() => {
    const loadExames = async () => {
      if (selectedNomeComercial && cpfColab && tipoAso && atendimento) {
        setLoadingExames(true);
        try {
          const exames = await fetchExames({
            cpf_colaborador: cpfColab,
            tipo_aso: tipoAso
          });

          // Filtra os exames que já foram adicionados em outros credenciados
          const examesAdicionados = getExamesAdicionados();
          const examesDisponiveis = exames.filter(
            (exame: ISelectedExames) =>
              !examesAdicionados.some(
                (added) => added.id_exame_ghe === exame.id_exame_ghe
              )
          );

          setLeft(examesDisponiveis);
        } catch (error) {
          console.error('Erro ao carregar exames:', error);
        } finally {
          setLoadingExames(false);
        }
      } else {
        setLeft([]);
        setRight([]);
        setChecked([]);
      }
    };

    loadExames();
  }, [
    selectedNomeComercial,
    cpfColab,
    tipoAso,
    atendimento,
    getExamesAdicionados,
    fetchExames
  ]);

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Paper className={classes.paper}>
        <Typography variant="h4" align="center" className={classes.title}>
          Agendamento Híbrido
        </Typography>

        {/* Seção Status */}
        <Typography variant="h5" className={classes.sectionTitle}>
          Status
        </Typography>
        <div className={classes.formRow}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusAgendamento}
              onChange={(e) => setStatusAgendamento(e.target.value as string)}
            >
              {status.map((status, index) => (
                <MenuItem key={index} value={status.value}>
                  {status.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Seção Colaborador */}
        <Typography variant="h5" className={classes.sectionTitle}>
          Colaborador
        </Typography>

        <div className={classes.formRow}>
          <div style={{ position: 'relative', width: '100%' }}>
            <InputMask
              mask="999.999.999-99"
              maskChar={null}
              value={cpfColab}
              onChange={handleCpfChange}
              disabled={loadingColab}
            >
              {(inputProps: any) => (
                <TextField
                  {...inputProps}
                  label="CPF"
                  variant="outlined"
                  fullWidth
                  placeholder="000.000.000-00"
                  error={!!messageError}
                  helperText={messageError}
                  InputProps={{
                    endAdornment: loadingColab && <CircularProgress size={24} />
                  }}
                />
              )}
            </InputMask>
          </div>

          <TextField
            label="Nome"
            variant="outlined"
            fullWidth
            value={nomeColab}
            disabled
          />
        </div>

        {/* Seção Tipo de ASO */}
        <div className={classes.formRow}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Tipo de ASO</InputLabel>
            <Select
              label="Tipo de ASO"
              value={tipoAso}
              onChange={(e) => setTipoAso(e.target.value as string)}
            >
              {ASO.map((aso, index) => (
                <MenuItem key={index} value={aso.value}>
                  {aso.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Seção Observações */}
        <div className={classes.formRow}>
          <TextField
            label="Observações"
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            value={obs}
            onChange={(e) => setObs(e.target.value)}
          />
        </div>

        {isHideCredential && (
          <>
            {/* Seção Tipo de Atendimento */}
            <Typography variant="h5" className={classes.sectionTitle}>
              Tipo de Atendimento
            </Typography>
            <div className={classes.formRow}>
              <RadioGroup
                row
                value={atendimento}
                onChange={(e) => setAtendimento(e.target.value)}
              >
                <FormControlLabel
                  value="telemedicina"
                  control={<Radio color="primary" />}
                  label="Telemedicina"
                />
                <FormControlLabel
                  value="presencial"
                  control={<Radio color="primary" />}
                  label="Presencial"
                />
              </RadioGroup>
            </div>

            {atendimento && (
              <>
                {/* Seção Credenciado */}
                <div className={classes.formRow}>
                  <Autocomplete
                    fullWidth
                    options={returListaNomeComercial?.data || []}
                    getOptionLabel={(option: INomeComercial) =>
                      `${option.nome_comercial} - ${option.estado}/${option.cidade}`
                    }
                    onChange={(_, newValue) =>
                      setSelectedNomeComercial(newValue)
                    }
                    value={selectedNomeComercial}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Credenciado"
                        variant="outlined"
                        placeholder="Selecione um credenciado"
                      />
                    )}
                  />
                </div>

                {/* Seção Data/Horário */}
                <div className={classes.formRow}>
                  <TextField
                    label="Data"
                    type="date"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={dataAgendamento}
                    onChange={(e) => setDataAgendamento(e.target.value)}
                  />
                  <TextField
                    label="Horário"
                    type="time"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={horarioAgendamento}
                    onChange={(e) => setHorarioAgendamento(e.target.value)}
                    disabled={
                      ordemChegada === 'Sim' && atendimento !== 'telemedicina'
                    }
                  />
                  {atendimento === 'telemedicina' && (
                    <TextField
                      label="Link"
                      variant="outlined"
                      fullWidth
                      value={linkDoAtendimento}
                      onChange={(e) => setLinkDoAtendimento(e.target.value)}
                    />
                  )}
                </div>

                {atendimento === 'presencial' && (
                  <div className={classes.formRow}>
                    <FormControl component="fieldset" fullWidth>
                      <Typography>Ordem de chegada</Typography>
                      <RadioGroup
                        row
                        value={ordemChegada}
                        onChange={(e) => setOrdemChegada(e.target.value)}
                      >
                        <FormControlLabel
                          value="Sim"
                          control={<Radio color="primary" />}
                          label="Sim"
                        />
                        <FormControlLabel
                          value="Não"
                          control={<Radio color="primary" />}
                          label="Não"
                        />
                      </RadioGroup>
                    </FormControl>
                  </div>
                )}

                {/* Lista de Transferência */}
                <div className={classes.transferContainer}>
                  <TransferList
                    title="Exames Disponíveis"
                    items={left}
                    checked={checked}
                    onToggle={handleToggle}
                    onToggleAll={handleToggleAll}
                  />

                  {loadingExames && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1
                      }}
                    >
                      <CircularProgress />
                    </div>
                  )}

                  <div className={classes.transferButtons}>
                    <Button
                      variant="contained"
                      className={classes.transferButton}
                      onClick={handleCheckedRight}
                      disabled={
                        checked.filter((item) => left.includes(item)).length ===
                          0 || loadingExames
                      }
                    >
                      <ChevronRightIcon />
                    </Button>
                    <Button
                      variant="contained"
                      className={classes.transferButton}
                      onClick={handleCheckedLeft}
                      disabled={
                        checked.filter((item) => right.includes(item))
                          .length === 0 || loadingExames
                      }
                    >
                      <ChevronLeftIcon />
                    </Button>
                  </div>

                  <TransferList
                    title="Exames Selecionados"
                    items={right}
                    checked={checked}
                    onToggle={handleToggle}
                    onToggleAll={handleToggleAll}
                  />
                </div>

                <div style={{ textAlign: 'right', marginTop: 16 }}>
                  <Button
                    variant="contained"
                    className={classes.buttonPrimary}
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddCredential}
                    disabled={right.length === 0}
                  >
                    Adicionar ao Agendamento
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {/* Credenciados Adicionados */}
        {credentialArray.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Exames Adicionados
            </Typography>
            {credentialArray.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ textAlign: 'center', margin: 16 }}
              >
                Nenhum exame adicionado ainda
              </Typography>
            ) : (
              credentialArray.map((credential) => (
                <Card
                  key={credential.id_exame_hibrido}
                  style={{ marginBottom: 16 }}
                >
                  <div style={{ padding: 16, position: 'relative' }}>
                    <IconButton
                      style={{ position: 'absolute', top: 8, right: 8 }}
                      onClick={() =>
                        handleRemoveCredential(credential.id_exame_hibrido)
                      }
                    >
                      <DeleteIcon color="error" />
                    </IconButton>

                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      {credential.nome_comercial}
                    </Typography>
                    <Typography variant="body2">
                      {credential.data_agendamento} às {credential.horario}
                    </Typography>
                    {credential.tipo === 'presencial' && (
                      <Typography variant="body2">
                        Ordem de chegada:{' '}
                        {credential.ordem_chegada || 'Não informado'}
                      </Typography>
                    )}

                    <Typography
                      variant="body2"
                      style={{ marginTop: 8, fontWeight: 500 }}
                    >
                      Exames selecionados ({credential.exames.length}):
                    </Typography>
                    <List dense style={{ maxHeight: 200, overflow: 'auto' }}>
                      {credential.exames.map((exame) => (
                        <ListItem key={exame.id_exame_ghe} divider>
                          <ListItemText
                            primary={exame.nome}
                            secondary={`Código GHE: ${exame.cod_ghe}`}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {credential.linkDoatendimento && (
                      <Typography variant="body2" style={{ marginTop: 8 }}>
                        <strong>Link do atendimento:</strong>{' '}
                        {credential.linkDoatendimento}
                      </Typography>
                    )}
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Botões de Ação */}
        <div className={classes.actionButtons}>
          <Button
            variant="outlined"
            className={classes.buttonSecondary}
            component={Link}
            to="/agendamentos"
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            className={classes.buttonPrimary}
            onClick={sendAgendamento}
            disabled={credentialArray.length === 0}
          >
            Finalizar Agendamento
          </Button>
        </div>
      </Paper>

      <Snackbar
  open={snack}
  autoHideDuration={6000}
  onClose={() => setSnack(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
>
  <MuiAlert
    elevation={6}
    variant="filled"
    severity={severity}
    onClose={() => setSnack(false)}
  >
    {message}
  </MuiAlert>
</Snackbar>
    </Container>
  );
};
